// Typed versions of null, help with type inference
export const nullS : null | string = null;
export const nullN : null | number = null;
export const nullO : null | object = null;
export const nullA : null | any = null;
export const nullComponent : null | React.ComponentType<any> = null;
export const nullElement : null | React.ReactElement = null;

// Typed versions of undefined, help with type inference
export const undefS : undefined | string = undefined;
export const undefSL : undefined | string[] = undefined;
export const undefN : undefined | number = undefined;
export const undefO : undefined | object = undefined;
export const undefA : undefined | any = undefined;
export const undefR : undefined | Record<string, any> = undefined;
export const undefB : undefined | boolean = undefined;
export const undefE : undefined | React.ReactElement = undefined;
export const undefC : undefined | React.ComponentType<any> = undefined;
export const undefF : undefined | (() => void) = undefined;

export const emptyR : Record<string, any> = {};

// A persona is a publicly visible identity of a user
export type Persona = {
    key: string;
    name: string;
    photoUrl?: string;
    hue?: number;
    linked?: boolean;
}

// A feature provides values for config slots exposed by a structure or parent feature.
// It may also expose new config slots that sub features can provide values for.
// Most work is done in features.
export type Feature<TProvides=any, TReceives=any> = {
    name: string;
    key: string;
    dev?: boolean;
    providesConfig?: Partial<TProvides>;
    receivesConfigDefaults?: TReceives;
    subscreens?: Record<string, React.ComponentType<any>>;
    missingConfigAllowed?: string[];
    parentFeature?: string;
    capabilities?: string[];
}

// A structure defines a type of instance (e.g. question) and provdes config slots that
// features can use to provide functionality.
export type Structure<T> = {
    key: string;
    name: string;
    screen: React.ComponentType<any>;
    receivesConfigDefaults?: T;
    hasFocusTrap?: boolean;
    subscreens?: Record<string, React.ComponentType<any>>;
    teaser?: React.ComponentType<any>;
    overlayScreen?: React.ComponentType<any>;
    capabilities?: string[];
    isAdminUi?: boolean;
}

export type FirebaseConfig = {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
}

export type SiloDefaultFeatures = {
    [structureKey: string]: Record<string, boolean>;
}

export type DeploymentConfig = {
    host?: string;
    firebaseConfig: FirebaseConfig;
    storagePrefix?: string;
    emulateFirebase?: boolean;
    defaultToAdminDash?: boolean;
    defaultFeatures?: Record<string, SiloDefaultFeatures>;
    adminEmailDomain?: string;
    ssoDebug?: boolean;
    publicWebPushKey?: string | null;
    pushNotificationsOnParentWindow?: boolean;
    siloConfig?: Record<string, SiloConfig>;
    skipLoginProviderScreen?: boolean;
}

export type SiloConfig = {
    themeKey?: string;
    defaultAvatarColors?: string[];
    hideCookieNoticeBanner?: boolean;
}

export type Table<T> = string & {__type: 'table', __T: T}
export type DerivedTable<T> = string & {__type: 'derived', __T: T}

export function declareTable<T>(typeName : string) : Table<T> {
    return typeName as Table<T>;
}
export const personaTable = declareTable<Persona>('persona');

// An extended table is a view of an existing table that has been extended with new columns.
// A feature will sometimes extend a table to store new information.
export function declareExtendedTable<T, U extends T>(baseTable: Table<T>) : Table<U> {
    return baseTable as Table<U>;
}

// A derived table is a server-generated table that is typical generated
// from tables in other instances, using server/derived-views
export function declareDerivedTable<T>(typeName: string) : DerivedTable<T> {
    return typeName as DerivedTable<T>;
}

export type DataReadOptions = {
    defaultValue?: any;
    limit?: number;
    oldest?: boolean;
    once?: boolean;
    memoized?: boolean;
}
